import { useState } from "react"
import { assetsFromManyRegisters } from "routes/asset-register/controller/assets-from-many-registers"
import { useDialog } from "lib/@hooks/useDialog"
import { EditAssetDialog } from "routes/regime/assets/edit-asset-dialog"
import { Box, Card, Grid, useTheme } from "@mui/material"
import { updateAssetRegister } from "routes/asset-register/controller/update-asset-register"
import { useParams } from "react-router-dom"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { Bound } from "lib/@components/binding/Bound"
import { FilterList } from "routes/asset-register/filter-list"
import { useReferenceState } from "lib/@hooks/use-reference"
import { AssetPaginationBottom, AssetPaginationTop } from "slot-definitions"
import { busyWhile } from "lib/@components/busy/busy-while"
import { withBusyMessage } from "lib/graphql/with-busy-message"
import { AssetPagination } from "./asset-pagination"
import { AssetsTableToolsRow } from "./assets-table-tools-row"
import { NoResults } from "lib/@components/no-results/no-results"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { refreshOnlyFor } from "lib/graphql/api"
import { getAssetCount } from "routes/asset-register/controller/get-asset-count"
import { ColumnManagerProvider } from "lib/@components/managed-columns/column-manager-provider"
import { AssetRegisterTable } from "./asset-register-table/asset-register-table"
import { useAssetRegisterColumns } from "./asset-register-table/use-asset-register-columns"

export function AssetList() {
    const { id } = useParams()
    return !!id && <InternalAssetList key={id} id={id} />
}

function InternalAssetList({ id }) {
    const manager = useAssetRegisterColumns(id)
    const [take, setTake] = useReferenceState(`asset-page-size-${id}`, 10)
    const refresh = useRefresh()
    const theme = useTheme()
    const [search] = useSearchParam("search")
    const [skip, setSkip] = useState(0)
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [filters, setFilters] = useReferenceState("asset-filters" + id, {})
    const sorter = `asset-list-sort-${id}`
    const [sortArr] = useReferenceState(sorter, [], "asset-sort")
    const raw = sortArr[0] || ""
    const sortParams = raw.startsWith("-")
        ? { sort: raw.slice(1), descending: true }
        : raw
          ? { sort: raw, descending: false }
          : {}
    if (!id) {
        console.error("Internal Asset List with no id")
    }
    const assetsResult = assetsFromManyRegisters.useResults(
        {
            skip,
            take,
            search,
            ...sortParams,
            ...filters,
            registers: [id],
        },
        withBusyMessage("Loading Assets").delay(100),
        refreshOnlyFor(id)
    )
    const { items = [], totalCount: filteredCount = 0 } = assetsResult ?? {}
    const assetsLoaded = assetsResult != null
    const totalAssetCount = getAssetCount.useResults(id)
    const hasActiveSearch = hasSearch(search)
    const hasActiveFilters = isFilterOpen && hasFilters(filters)

    const createAssetDialog = useDialog(<EditAssetDialog register={id} />, { maxWidth: "md" })

    AssetPaginationTop.usePlug(<AssetPagination />, [
        take,
        setTake,
        skip,
        setSkip,
        filteredCount,
        totalAssetCount,
        hasActiveSearch,
        hasActiveFilters,
    ])
    AssetPaginationBottom.usePlug(<AssetPagination />, [
        take,
        setTake,
        skip,
        setSkip,
        filteredCount,
        totalAssetCount,
        hasActiveSearch,
        hasActiveFilters,
    ])

    return (
        <ColumnManagerProvider manager={manager} scopeClass="asset-register-columns">
            <Bound
                items={items}
                assetsLoaded={assetsLoaded}
                totalCount={filteredCount}
                search={search}
                createAsset={add}
                setFilters={setFilters}
                filters={filters}
                isFilterOpen={isFilterOpen}
                handleFilterClick={handleFilterClick}
                sorter={sorter}
                setSkip={setSkip}
            >
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={isFilterOpen ? 9 : 12}>
                        <Card sx={{ p: 4.5, mb: 3, mt: 2, radius: 2 }}>
                            <AssetsTableToolsRow />
                            <AssetPaginationTop.Slot
                                take={take}
                                setTake={setTake}
                                skip={skip}
                                setSkip={setSkip}
                                totalCount={filteredCount}
                                totalAssetCount={totalAssetCount}
                                hasActiveSearch={hasActiveSearch}
                                hasActiveFilters={hasActiveFilters}
                                showCount={true}
                            />
                            <If truthy={items.length === 0}>
                                <NoResults
                                    children="No Data"
                                    childrenStyle={{ fontWeight: 700, fontSize: "1.25rem" }}
                                />
                                <Else>
                                    <AssetRegisterTable register={id} take={take} setSkip={setSkip} />
                                </Else>
                            </If>
                            <Box sx={{ pt: 2 }}>
                                <AssetPaginationBottom.Slot
                                    take={take}
                                    setTake={setTake}
                                    skip={skip}
                                    setSkip={setSkip}
                                    totalCount={filteredCount}
                                    totalAssetCount={totalAssetCount}
                                    hasActiveSearch={hasActiveSearch}
                                    hasActiveFilters={hasActiveFilters}
                                    showCount={false}
                                />
                            </Box>
                        </Card>
                    </Grid>
                    {isFilterOpen ? (
                        <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{
                                overflow: "hidden",
                                backgroundColor: theme.palette.background.neutral,
                                borderRadius: 2,
                                px: 3,
                                pb: 1,
                                marginTop: 4,
                            }}
                        >
                            <FilterList isFilterVisible={isFilterOpen} handleClick={handleFilterClick} />
                        </Grid>
                    ) : null}
                </Grid>
            </Bound>
        </ColumnManagerProvider>
    )

    async function add() {
        const asset = await createAssetDialog()
        if (asset) {
            await busyWhile(async () => updateAssetRegister({ registerId: id, updateAssets: [asset] }), "Adding asset")
            refresh()
        }
    }

    function handleFilterClick() {
        setIsFilterOpen((prevState) => !prevState)
    }

    function hasFilters(value) {
        return Object.keys(value ?? {}).length > 0
    }

    function hasSearch(value) {
        return !!value?.trim?.()
    }
}
